import React, { useState, useEffect, useRef } from 'react';
import { Card, Row, Col, Button, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';

import SubtleBadge from 'components/common/SubtleBadge';

import { manifestApi } from 'api';

const ManifestShipmentSummaryPanel = ({
  manifestId,
  onManifestUpdate,
  shipmentCount,
}) => {
  const shipmentPanelRef = useRef(null);
  const barcodeInputRef = useRef(null);

  const [isOpen, setIsOpen] = useState(true);
  const [barcode, setBarcode] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shipmentData, setShipmentData] = useState(null);

  useEffect(() => {
    const handleClickOutside = event => {
      if (
        shipmentPanelRef.current &&
        !shipmentPanelRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setShipmentData(null);
      }
    };

    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.addEventListener('mousedown', handleClickOutside);

      return () => {
        document.body.style.overflow = 'unset';
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && !isSubmitting) {
      barcodeInputRef.current?.focus();
    }
  }, [isOpen, isSubmitting]);

  const handleSubmit = async e => {
    e.preventDefault();
    if (!barcode.trim() || isSubmitting) return;

    try {
      setIsSubmitting(true);
      const response = await manifestApi.addShipmentToManifest(
        manifestId,
        barcode.trim(),
      );

      setShipmentData(response);

      if (response.success) {
        toast.success('Shipment added successfully');
        onManifestUpdate?.();
      } else {
        toast.error(response.error || 'Failed to add shipment');
      }
    } catch (error) {
      toast.error('Failed to add shipment');
    } finally {
      setIsSubmitting(false);
      setBarcode('');
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setShipmentData(null);
  };

  if (!isOpen) {
    return (
      <div
        className="position-fixed bottom-0 end-0 p-3"
        style={{ zIndex: 1041 }}
      >
        <Button variant="dark" size="sm" onClick={() => setIsOpen(true)}>
          Shipment Summary <FontAwesomeIcon icon="plus" />
        </Button>
      </div>
    );
  }

  return (
    <>
      <div
        className="position-fixed top-0 start-0 w-100 h-100"
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          zIndex: 1040,
        }}
      />

      <div
        className="position-fixed bottom-0 start-0 w-100"
        style={{ zIndex: 1041 }}
        ref={shipmentPanelRef}
      >
        <div className="container px-6">
          <Card>
            <Card.Header className="bg-dark text-white py-2">
              <div className="d-flex justify-content-between align-items-center">
                <strong>Shipment Summary</strong>

                <div className="d-flex align-items-center gap-3">
                  <div className="text-end">
                    <span className="me-2">
                      No. of shipments successfully added :
                    </span>
                    <span className="fs-7">{shipmentCount}</span>
                  </div>

                  {shipmentData && (
                    <SubtleBadge
                      pill={true}
                      bg={shipmentData?.success ? 'success' : 'danger'}
                      className="px-3 py-1"
                    >
                      Status: {shipmentData?.success ? 'Success' : 'Fail'}
                    </SubtleBadge>
                  )}

                  <FontAwesomeIcon
                    className="cursor-pointer text-white"
                    icon="times"
                    onClick={handleClose}
                  />
                </div>
              </div>
            </Card.Header>

            <Card.Body>
              <Row className="fs-10 g-3">
                <Col xs={6}>
                  <div className="d-flex gap-2">
                    <strong>Courier Provider</strong>
                    <span>
                      {shipmentData?.shipment?.shipping_provider
                        ?.shipping_provider_type || '—'}
                    </span>
                  </div>
                </Col>

                <Col xs={6}>
                  <div className="d-flex gap-2">
                    <strong>Shipping Region</strong>
                    <span>
                      {shipmentData?.shipment?.shipping_region || '—'}
                    </span>
                  </div>
                </Col>

                <Col xs={6}>
                  <div className="d-flex gap-2">
                    <strong>Shipment Code</strong>
                    <span>{shipmentData?.shipment?.shipment_code || '—'}</span>
                  </div>
                </Col>

                <Col xs={6}>
                  <div className="d-flex gap-2">
                    <strong>No. of Products</strong>
                    <span>
                      {shipmentData?.shipment?.invoice_product_total_quantity ||
                        '—'}
                    </span>
                  </div>
                </Col>

                <Col xs={6}>
                  <div className="d-flex gap-2">
                    <strong>AWB Number</strong>
                    <span>{shipmentData?.shipment?.awb_number || '—'}</span>
                  </div>
                </Col>
              </Row>

              {shipmentData?.shipment?.product_summary?.length > 0 && (
                <div className="mt-3">
                  <table className="table table-sm table-bordered mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th className="small">Product SKU</th>
                        <th className="small">Product Name</th>
                        <th className="small">Qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shipmentData?.shipment.product_summary.map(
                        (item, index) => (
                          <tr key={index}>
                            <td className="small">
                              {item?.product_sku || '-'}
                            </td>
                            <td className="small">
                              {item?.product_name || '-'}
                            </td>
                            <td className="small">{item?.quantity}</td>
                          </tr>
                        ),
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </Card.Body>

            <Card.Footer className="p-2 bg-white">
              <Form onSubmit={handleSubmit}>
                <Form.Control
                  ref={barcodeInputRef}
                  size="sm"
                  placeholder="Scan Shipment Code/ AWB number"
                  value={barcode}
                  onChange={e => setBarcode(e.target.value)}
                  disabled={isSubmitting}
                />
              </Form>
            </Card.Footer>
          </Card>
        </div>
      </div>
    </>
  );
};

export default ManifestShipmentSummaryPanel;
